import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import { connect } from "react-redux";
import styled from "styled-components";

const enum fields {
    eyebrow = "eyebrow",
    heading = "heading",
    introduction = "introduction",
    services = "services",
    accentColor = "accentColor",
    textColor = "textColor",
    borderColor = "borderColor",
    backgroundColor = "backgroundColor",
    arrow = "arrow",
}

interface ServiceFields {
    title: string;
    description: string;
    link?: LinkFieldValue;
}

interface ServiceItem extends HasFields {
    fields: ServiceFields;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.eyebrow]: string;
        [fields.heading]: string;
        [fields.introduction]: string;
        [fields.services]: ServiceItem[];
        [fields.accentColor]: string;
        [fields.textColor]: string;
        [fields.borderColor]: string;
        [fields.backgroundColor]: string;
        [fields.arrow]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    services: (ownProps.fields.services || []).map(service => ({
        fields: service.fields,
        url: service.fields.link ? getLink(state, service.fields.link)?.url : undefined,
    })),
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

interface ServiceContentProps {
    fields: ServiceFields;
    arrow: string;
}

const ServiceContent: React.FC<ServiceContentProps> = ({ fields: service, arrow }) => (
    <>
        <ServiceCopy>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
        </ServiceCopy>
        {arrow && <Arrow src={arrow} alt="" aria-hidden="true" />}
    </>
);

const ServicesThatEarnNextCall: React.FC<Props> = ({ fields: content, services }) => {
    return (
        <Section backgroundColor={content.backgroundColor} textColor={content.textColor}>
            <Introduction>
                <Eyebrow accentColor={content.accentColor}>{content.eyebrow}</Eyebrow>
                <Heading>{content.heading}</Heading>
                <IntroText>{content.introduction}</IntroText>
            </Introduction>
            <ServiceList>
                {services.map((service, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <ServiceRow key={index} borderColor={content.borderColor}>
                        {service.url ? (
                            <ServiceLink href={service.url}>
                                <ServiceContent fields={service.fields} arrow={content.arrow} />
                            </ServiceLink>
                        ) : (
                            <ServiceContent fields={service.fields} arrow={content.arrow} />
                        )}
                    </ServiceRow>
                ))}
            </ServiceList>
        </Section>
    );
};

const definition: WidgetDefinition = {
    group: "Common",
    fieldDefinitions: [
        {
            name: fields.eyebrow,
            displayName: "Eyebrow",
            editorTemplate: "TextField",
            defaultValue: "WHAT WE DO",
            fieldType: "General",
        },
        {
            name: fields.heading,
            displayName: "Heading",
            editorTemplate: "TextField",
            defaultValue: "SERVICES THAT EARN THE NEXT CALL",
            fieldType: "General",
        },
        {
            name: fields.introduction,
            displayName: "Introduction",
            editorTemplate: "TextField",
            defaultValue: "Beyond moving boxes — we put the expertise on the ground at your plant.",
            fieldType: "General",
        },
        
        {
            name: fields.accentColor,
            displayName: "Accent Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#e63946",
            fieldType: "General",
        },
        {
            name: fields.textColor,
            displayName: "Text Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#3f4448",
            fieldType: "General",
        },
        {
            name: fields.borderColor,
            displayName: "Divider Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#c8c8c8",
            fieldType: "General",
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "#ffffff",
            fieldType: "General",
        },
        {
            name: fields.arrow,
            displayName: "Service Arrow",
            editorTemplate: "ImagePickerField",
            defaultValue: "",
            fieldType: "General",
        },
        {
            name: fields.services,
            displayName: "Services",
            editorTemplate: "ListField",
            defaultValue: [
                {
                    fields: {
                        title: "HIGH-LEVEL APPLICATION SUPPORT",
                        description: "Spec the right product the first time. Our reps walk through the problem with you.",
                        link: { value: "", type: "Page" },
                    },
                },
                {
                    fields: {
                        title: "24/7 EMERGENCY SERVICE",
                        description: "After-hours direct line: 610-999-9702. Real people, fast dispatch.",
                        link: { value: "", type: "Page" },
                    },
                },
                {
                    fields: {
                        title: "IN-PLANT VENDOR MANAGED INVENTORY",
                        description: "We stock, scan, and replenish — your team never chases a part.",
                        link: { value: "", type: "Page" },
                    },
                },
                {
                    fields: {
                        title: "PRODUCT & MANUFACTURER TRAINING",
                        description: "From complimentary on-site sessions to full-day off-site seminars.",
                        link: { value: "", type: "Page" },
                    },
                },
            ],
            fieldType: "General",
            getDisplay: (item: HasFields) => item.fields.title,
            fieldDefinitions: [
                {
                    name: "title",
                    displayName: "Service Title",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "description",
                    displayName: "Service Description",
                    editorTemplate: "TextField",
                    defaultValue: "",
                },
                {
                    name: "link",
                    displayName: "Service Link",
                    editorTemplate: "LinkField",
                    defaultValue: {
                        value: "",
                        type: "Page",
                    },
                },
            ],
        }
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ServicesThatEarnNextCall),
    definition,
};

export default widgetModule;

const Section = styled.section<{ backgroundColor: string; textColor: string }>`
    display: grid;
    grid-template-columns: minmax(260px, 0.85fr) minmax(0, 1.5fr);
    gap: clamp(36px, 4vw, 70px);
    width: 100%;
    padding: clamp(28px, 5vw, 64px) clamp(20px, 6vw, 20px);
    box-sizing: border-box;
    background: ${({ backgroundColor }) => backgroundColor || "#fff"};
    color: ${({ textColor }) => textColor || "#3f4448"};
    font-family: Arial, sans-serif;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
        gap: 34px;
    }
`;

const Introduction = styled.div`
    align-self: start;
    min-width: 0;
    padding-top: 18px;
`;

const Eyebrow = styled.div<{ accentColor: string }>`
    margin-bottom: 24px;
    color: ${({ accentColor }) => accentColor || "#e63946"};
    font-size: clamp(11px, 1.3vw, 14px);
    font-weight: 700;
    line-height: 1.2;
`;

const Heading = styled.h2`
    margin: 0;
    font-size: clamp(26px, 3vw, 38px);
    font-weight: 700;
    line-height: 0.98;
    letter-spacing: 0;
    text-transform: uppercase;
    overflow-wrap: anywhere;
`;

const IntroText = styled.p`
    margin: 28px 0 0;
    font-size: clamp(12px, 1.2vw, 15px);
    line-height: 1.45;
    overflow-wrap: anywhere;
`;

const ServiceList = styled.div`
    min-width: 0;
`;

const ServiceRow = styled.article<{ borderColor: string }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    min-width: 0;
    min-height: 74px;
    padding: 18px 8px 18px clamp(18px, 2.4vw, 44px);
    box-sizing: border-box;
    border-top: 1px solid ${({ borderColor }) => borderColor || "#c8c8c8"};

    
        &:last-child {
        border-bottom: 1px solid #000000 !important;
    }
        &:first-child {
        border-top: 1px solid #000000 !important;
    }

    @media (max-width: 480px) {
        align-items: flex-start;
        gap: 14px;
        padding-left: 0;
    }
`;

const ServiceLink = styled.a`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    width: 100%;
    min-width: 0;
    color: inherit;
    text-decoration: none;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const ServiceCopy = styled.div`
    min-width: 0;
`;

const ServiceTitle = styled.h3`
    margin: 0 0 8px;
    font-size: clamp(15px, 1.7vw, 20px);
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
`;

const ServiceDescription = styled.p`
    margin: 0;
    font-size: clamp(10px, 1vw, 12px);
    line-height: 1.35;
    overflow-wrap: anywhere;
`;

const Arrow = styled.img`
    display: block;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;
    object-fit: contain;
`;
